import { useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, For, Show } from "solid-js";
import { api } from "../api";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { OperatedBy } from "../components/OperatedBy";
import { Toggle } from "../components/Toggle";
import { nextStepFor, session } from "../state/session";

const ALERTS_KEY = "parking-alerts";
function readAlerts(): boolean {
  try {
    return localStorage.getItem(ALERTS_KEY) !== "0";
  } catch {
    return true;
  }
}

/** Figma 04 Service activation: one-shot checklist + terms. */
export function Activate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [terms, setTerms] = createSignal(false);
  // No backend for alerts yet: the choice is kept on this device only.
  const [alerts, setAlertsSignal] = createSignal(readAlerts());
  const setAlerts = (on: boolean) => {
    setAlertsSignal(on);
    try {
      localStorage.setItem(ALERTS_KEY, on ? "1" : "0");
    } catch {
      // private mode / blocked storage: the toggle still works for this session
    }
  };
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string>();
  const vehicle = () => (typeof params.vehicle === "string" ? params.vehicle : undefined);

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api.post("/me/activate", { acceptTerms: true });
      await session.refetch();
      navigate(nextStepFor(session.me(), vehicle()), { replace: true });
    } catch {
      setError("Activation impossible pour le moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main class="screen bg-surface">
      <AppBar title="Vélo en libre-service" back="/map" />
      <div class="screen-body px-[15px] pt-[22px] short:pt-3">
        <h1 class="px-[5px] text-[21px] font-medium leading-[1.25] tracking-[0.21px] text-violet">
          Activez le vélo en une étape
        </h1>
        <p class="mt-2 px-[5px] text-[15px] leading-[1.25] tracking-[0.15px] text-text">
          Pas de nouveau compte à créer : votre compte M'Ticket suffit pour rouler.
        </p>
        <ul class="mt-[10px] rounded-xl border border-border bg-white px-4 py-[6px] divide-y divide-border">
          <For each={CHECKLIST}>
            {(it) => (
              <li class="flex items-start gap-3 py-[10px] short:py-[6px]">
                <img src="/icons/check-circle.svg" width="22" height="22" alt="" class="shrink-0" />
                <div class="flex flex-col gap-[2px]">
                  <div class="text-sm font-medium leading-[1.25] tracking-[0.14px] text-strong">
                    {it.title}
                  </div>
                  <div class="text-[13px] leading-[1.25] tracking-[0.13px] text-text">
                    {it.detail}
                  </div>
                </div>
              </li>
            )}
          </For>
        </ul>
        <label class="mt-[18px] flex cursor-pointer items-start gap-3 px-[5px] short:mt-3">
          <input
            type="checkbox"
            class="peer sr-only"
            checked={terms()}
            onChange={(e) => setTerms(e.currentTarget.checked)}
          />
          {/* Figma tam/checkbox: 22 px, violet, radius 3, white 18 px tick. */}
          <span
            class={`h-[22px] w-[22px] shrink-0 rounded-[3px] flex items-center justify-center peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-violet peer-focus-visible:outline-offset-2 ${terms() ? "bg-violet" : "border-2 border-text bg-white"}`}
            aria-hidden="true"
          >
            <Show when={terms()}>
              <img src="/icons/check-white-18.svg" width="18" height="18" alt="" />
            </Show>
          </span>
          <span class="text-sm leading-[1.25] tracking-[0.14px] text-text">
            J'accepte les conditions générales d'utilisation du service vélo
          </span>
        </label>
        <div class="mt-[17px] flex items-start gap-3 px-[5px] short:mt-3">
          <Toggle checked={alerts()} onChange={setAlerts} label="Alertes de stationnement" />
          <div>
            <div class="text-sm font-medium leading-[1.25] tracking-[0.14px] text-strong">
              Alertes de stationnement
            </div>
            <div class="mt-[2px] text-xs leading-[1.25] tracking-[0.12px] text-text">
              Zones interdites lors des évènements (matchs, marchés)
            </div>
          </div>
        </div>
        <Show when={error()}>
          {(m) => (
            <p class="mt-3 px-[5px] text-sm text-[#e53935]" role="alert">
              {m()}
            </p>
          )}
        </Show>
        <div class="screen-footer">
          <OperatedBy class="mb-4" />
          <Button disabled={!terms()} loading={busy()} onClick={submit}>
            Continuer
          </Button>
        </div>
      </div>
    </main>
  );
}

/** Prices come from the Pony GBFS feed, shown on the map: no fixed tariff claim here. */
const CHECKLIST = [
  { title: "Compte M'Ticket", detail: "Connecté" },
  {
    title: "Tarifs affichés sur la carte",
    detail: "Le prix de chaque vélo est indiqué avant de rouler",
  },
  { title: "Justificatif", detail: "Déjà vérifié avec votre Pass gratuité" },
  { title: "Stationnement", detail: "Dans les zones de stationnement de la carte" },
];
